import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { slugify, type Category, type Product } from '@eprintsmart/shared';
import {
  archiveProduct,
  buildCategoryTree,
  deleteCategory,
  indexProductsByCategory,
  productsWithoutCategory,
  unlistedProducts,
  restoreProduct,
  saveCategory,
  subscribeToCategories,
  subscribeToProducts,
  type CategoryNode,
} from '../services/catalog';
import { ProductImagesDialog } from '../components/ProductImagesDialog';
import { TreeRow, computeStats, useSearchMatches, type TreeStats } from './CategoryTreeView';

export function CategoryTree() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<Partial<Category> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [imagesFor, setImagesFor] = useState<Product | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => subscribeToCategories((next) => {
    setCategories(next);
    setLoading(false);
  }), []);

  useEffect(() => subscribeToProducts(setProducts), []);

  const tree = useMemo(() => buildCategoryTree(categories), [categories]);

  // Each product is filed at its DEEPEST category only. Products denormalise
  // their whole ancestor chain into categoryIds, so indexing every id showed
  // the same product under the group, the category and the sub-category.
  const productsByCategory = useMemo(
    () => indexProductsByCategory(products, categories),
    [products, categories],
  );

  const orphans = useMemo(
    () => productsWithoutCategory(products, categories),
    [products, categories],
  );

  // Shown as a fact, not a warning: these are outside the tree by design.
  const unlisted = useMemo(() => unlistedProducts(products), [products]);

  // Rollup counts, computed once for the whole tree rather than per row.
  const stats = useMemo(() => {
    const out = new Map<string, TreeStats>();
    tree.forEach((n) => computeStats(n, productsByCategory, out));
    return out;
  }, [tree, productsByCategory]);

  const { matches, autoExpand } = useSearchMatches(tree, productsByCategory, search);

  // Searching opens the path to every hit; without this a match three levels
  // down is invisible behind collapsed parents.
  useEffect(() => {
    if (autoExpand.size > 0) setExpanded((prev) => new Set([...prev, ...autoExpand]));
  }, [autoExpand]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandAll = () => setExpanded(new Set(categories.map((c) => c.id)));
  const collapseAll = () => setExpanded(new Set());

  const handleArchive = async (product: Product) => {
    // Confirm, matching Reels and Banners. The hour matters: every storefront
    // route is revalidate = 3600, so this is not instant and saying otherwise
    // invites someone to archive twice thinking it failed.
    if (!confirm(
      `Archive "${product.name}"? It stops appearing on the storefront within the hour. ` +
      'You can restore it from here or the product editor.',
    )) return;
    setError(null);
    try {
      await archiveProduct(product.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not archive that product.');
    }
  };

  const handleRestore = async (product: Product) => {
    setError(null);
    try {
      // Back to DRAFT, never straight to ACTIVE: un-archiving should not
      // republish something nobody has re-checked.
      await restoreProduct(product.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not restore that product.');
    }
  };

  const handleDelete = async (node: CategoryNode) => {
    setError(null);
    try {
      await deleteCategory(node.id, categories);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete that category.');
    }
  };

  const liveCats = categories.filter((c) => c.status === 'ACTIVE').length;
  const liveProds = products.filter((p) => p.status === 'ACTIVE').length;
  const visibleRoots = matches ? tree.filter((n) => matches.has(n.id)) : tree;

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="baseline" spacing={2} flexWrap="wrap">
        <Typography variant="h1" sx={{ flexGrow: 1 }}>Catalogue</Typography>
        {/* Split live from archived. A single total silently mixes the two and
            then disagrees with the storefront, which only ever renders ACTIVE —
            leaving you to wonder which of the two is wrong. */}
        <Typography variant="body2" color="text.secondary">
          {liveCats} categories · {liveProds} live products
          {products.length - liveProds > 0 && ` · ${products.length - liveProds} archived`}
        </Typography>
        <Button
          variant="contained"
          onClick={() => setEditing({ parentId: null, level: 0, status: 'ACTIVE', sortOrder: tree.length })}
        >
          New group
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          fullWidth
          placeholder="Search categories and products…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
        <Button size="small" startIcon={<UnfoldMoreIcon />} onClick={expandAll}>Expand all</Button>
        <Button size="small" startIcon={<UnfoldLessIcon />} onClick={collapseAll}>Collapse</Button>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      )}

      {orphans.length > 0 && (
        <Alert severity="warning">
          {orphans.length} product{orphans.length === 1 ? ' is' : 's are'} not in any category, so
          {orphans.length === 1 ? ' it does' : ' they do'} not appear below and
          {orphans.length === 1 ? ' is' : ' are'} unreachable on the storefront:{' '}
          {orphans.slice(0, 5).map((p) => p.name).join(', ')}
          {orphans.length > 5 && `, +${orphans.length - 5} more`}
        </Alert>
      )}

      {unlisted.length > 0 && (
        <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
          <Typography variant="caption">
            Not in the tree by design ({unlisted.length}):{' '}
            {unlisted.map((p) => p.name).join(', ')} — ordered directly rather than browsed,
            so {unlisted.length === 1 ? 'it needs' : 'they need'} no category.
          </Typography>
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : visibleRoots.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {search ? `Nothing matches "${search}".` : 'No categories yet.'}
          </Typography>
        </Paper>
      ) : (
        // Each top-level group is its own card. One long undivided list gave no
        // resting point between 16 groups and 302 categories.
        <Stack spacing={1.5}>
          {visibleRoots.map((node) => (
            <Paper key={node.id} variant="outlined" sx={{ p: 1, overflow: 'hidden' }}>
              <TreeRow
                node={node}
                depth={0}
                expanded={expanded}
                stats={stats}
                matches={matches}
                onToggle={toggle}
                onEdit={setEditing}
                onDelete={handleDelete}
                productsByCategory={productsByCategory}
                onImages={setImagesFor}
                onArchive={handleArchive}
                onRestore={handleRestore}
              />
            </Paper>
          ))}
        </Stack>
      )}

      <ProductImagesDialog
        product={imagesFor}
        open={Boolean(imagesFor)}
        onClose={() => setImagesFor(null)}
        // subscribeToProducts is live, so the row's image count updates itself;
        // this just closes the dialog once the save has landed.
        onSaved={() => setImagesFor(null)}
      />

      <CategoryDialog
        draft={editing}
        categories={categories}
        onClose={() => setEditing(null)}
      />
    </Stack>
  );
}

function CategoryDialog({
  draft,
  categories,
  onClose,
}: {
  draft: Partial<Category> | null;
  categories: Category[];
  onClose: () => void;
}) {
  const [form, setForm] = useState<Partial<Category>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setForm(draft ?? {}), [draft]);

  if (!draft) return null;

  const isNew = !draft.id;
  const set = <K extends keyof Category>(key: K, value: Category[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setError(null);
    const name = (form.name ?? '').trim();
    if (!name) return setError('A name is required.');

    const slug = (form.slug ?? slugify(name)).trim();
    if (isNew && categories.some((c) => c.slug === slug)) {
      return setError(`The slug "${slug}" is already used.`);
    }

    setSaving(true);
    try {
      await saveCategory(draft.id ?? slug, {
        ...form,
        name,
        slug,
        parentId: form.parentId ?? null,
        level: (form.level ?? 0) as 0 | 1 | 2,
        sortOrder: form.sortOrder ?? 0,
        status: form.status ?? 'ACTIVE',
        ...(isNew ? { createdAt: Date.now() } : {}),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? 'New category' : `Edit ${draft.name}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Name"
            value={form.name ?? ''}
            onChange={(e) => {
              set('name', e.target.value);
              if (isNew) set('slug', slugify(e.target.value));
            }}
            fullWidth
            autoFocus
          />

          <TextField
            label="Slug"
            value={form.slug ?? ''}
            onChange={(e) => set('slug', slugify(e.target.value))}
            fullWidth
            disabled={!isNew}
            helperText={
              isNew
                ? 'Becomes the page URL.'
                : 'Fixed after creation — changing it would break links and search rankings.'
            }
          />

          <TextField
            label="Description"
            value={form.description ?? ''}
            onChange={(e) => set('description', e.target.value)}
            fullWidth
            multiline
            minRows={2}
            helperText="Shown on the category page and used as the meta description fallback."
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Status"
              select
              value={form.status ?? 'ACTIVE'}
              onChange={(e) => set('status', e.target.value as Category['status'])}
              sx={{ flex: 1 }}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="DRAFT">Draft</MenuItem>
              <MenuItem value="ARCHIVED">Archived</MenuItem>
            </TextField>

            <TextField
              label="Sort order"
              type="number"
              value={form.sortOrder ?? 0}
              onChange={(e) => set('sortOrder', Number(e.target.value))}
              sx={{ width: 140 }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
